import SedecShell from "@/components/layout/SedecShell";
import { Card } from "@/components/ui/card";
import { Loader2, ShieldCheck, TriangleAlert, CircleOff, Clock } from "lucide-react";
import { useListComptesReversementReseau } from "@/lib/backoffice-hooks";
import type { CompteReversementReseau } from "@/lib/api";

/**
 * Qui peut être payé, qui ne peut pas — vue réseau des comptes de reversement.
 *
 * Les numéros restent masqués : le SEDEC pilote, il n'a pas à manipuler les RIB.
 * L'état « numéro partagé avec une autre école » est affiché aussi fort qu'une
 * absence de compte, parce qu'il est plus dangereux : un compte absent bloque un
 * reversement, un compte partagé l'envoie chez quelqu'un d'autre.
 */
const ETATS: Record<CompteReversementReseau["etat"], { label: string; classe: string; Icone: typeof ShieldCheck }> = {
  CONFIRME: { label: "Confirmé", classe: "bg-emerald-50 text-emerald-700", Icone: ShieldCheck },
  A_CONFIRMER: { label: "À confirmer par l'école", classe: "bg-amber-50 text-amber-800", Icone: Clock },
  NUMERO_PARTAGE_AVEC_UNE_AUTRE_ECOLE: {
    label: "Numéro partagé avec une autre école",
    classe: "bg-red-50 text-red-700",
    Icone: TriangleAlert,
  },
  AUCUN_COMPTE: { label: "Aucun compte", classe: "bg-slate-100 text-slate-600", Icone: CircleOff },
};

export default function SedecComptesReversement() {
  const { data, isLoading } = useListComptesReversementReseau();
  const lignes = data ?? [];

  const compte = (etat: CompteReversementReseau["etat"]) =>
    lignes.filter((l) => l.etat === etat).length;

  return (
    <SedecShell>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Comptes de reversement</h1>
          <p className="text-muted-foreground mt-1">
            Où l'argent encaissé pour chaque école lui est reversé. Les familles peuvent payer
            dans tous les cas ; une école sans compte confirmé attend son reversement : ce sont
            les écoles à relancer.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-4">
          {(Object.keys(ETATS) as CompteReversementReseau["etat"][]).map((etat) => {
            const { label, classe, Icone } = ETATS[etat];
            return (
              <Card key={etat} className="p-4">
                <div className="flex items-center gap-2">
                  <span className={`rounded p-1.5 ${classe}`}><Icone className="h-4 w-4" /></span>
                  <span className="text-2xl font-semibold">{compte(etat)}</span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{label}</p>
              </Card>
            );
          })}
        </div>

        {isLoading && (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b bg-slate-50 text-left">
              <tr>
                <th className="p-3 font-medium">École</th>
                <th className="p-3 font-medium">Compte</th>
                <th className="p-3 font-medium">Banque / opérateur</th>
                <th className="p-3 font-medium">Numéro</th>
                <th className="p-3 font-medium">Titulaire</th>
                <th className="p-3 font-medium">État</th>
              </tr>
            </thead>
            <tbody>
              {lignes.map((l, i) => {
                const { label, classe, Icone } = ETATS[l.etat];
                return (
                  <tr key={`${l.etablissementId}-${i}`} className="border-b last:border-0">
                    <td className="p-3 font-medium">{l.ecole}</td>
                    <td className="p-3">{l.libelle ?? "·"}</td>
                    <td className="p-3">{l.banque ?? "·"}</td>
                    <td className="p-3 font-mono">{l.numeroMasque ?? "·"}</td>
                    <td className="p-3">{l.titulaire ?? "·"}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs ${classe}`}>
                        <Icone className="h-3 w-3" /> {label}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {!isLoading && lignes.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-muted-foreground">
                    Aucun établissement.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>
    </SedecShell>
  );
}
